import { createPortal } from "react-dom";
import { NavLink, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { pendingBotProposalsCountQueryOptions } from "@/lib/api/botProposals";
import { bottomBarLabel, isNavGroupActive, NAV_GROUPS } from "@/components/layout/navConfig";

export const BOTTOM_NAV_CLEARANCE = "calc(5rem + env(safe-area-inset-bottom, 0px))" as const;

export const BOTTOM_NAV_OFFSET = "calc(0.75rem + env(safe-area-inset-bottom, 0px))" as const;

const tabBtnClass =
  "relative flex min-h-12 min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-xl px-0.5 py-1 text-center text-[10px] font-semibold leading-none text-slate-500 transition-colors hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-50";

function tabTestId(to: string): string {
  return `bottom-nav-${to.replace(/^\//, "").replace(/\//g, "-")}`;
}

function BottomNavBar() {
  const location = useLocation();
  const pendingQ = useQuery(pendingBotProposalsCountQueryOptions());
  const pending = pendingQ.data ?? 0;

  return (
    <nav
      data-testid="bottom-nav"
      aria-label="Alsó navigáció"
      className="pointer-events-none fixed inset-x-0 z-[50] flex justify-center px-2 lg:hidden print:hidden"
      style={{ bottom: BOTTOM_NAV_OFFSET }}
    >
      <div
        className={cn(
          "pointer-events-auto flex w-full max-w-3xl items-stretch justify-between gap-0 rounded-2xl border border-slate-200/80 bg-white/95 px-0.5 py-1 shadow-[0_8px_28px_rgba(15,23,42,0.12)] backdrop-blur-md lg:max-w-7xl",
          "dark:border-slate-700/80 dark:bg-slate-950/95 dark:shadow-[0_8px_28px_rgba(0,0,0,0.45)]"
        )}
      >
        {NAV_GROUPS.map((group) => {
          const active = isNavGroupActive(group, location.pathname, location.search);
          return (
            <NavLink
              key={group.to}
              to={group.to}
              {...(group.end ? { end: true } : {})}
              aria-label={group.label}
              title={group.label}
              data-testid={tabTestId(group.to)}
              className={cn(
                tabBtnClass,
                active && "bg-amber-100 text-amber-800 dark:bg-amber-950/70 dark:text-amber-300"
              )}
            >
              <group.Icon
                className={cn("h-5 w-5 shrink-0", active && "text-amber-600 dark:text-amber-400")}
                aria-hidden
              />
              <span className="max-w-full whitespace-nowrap">{bottomBarLabel(group)}</span>
              {group.badgeKey === "pendingProposals" && pending > 0 ? (
                <span
                  data-testid="bottom-nav-pending-badge"
                  className="absolute right-0.5 top-0.5 rounded-full bg-amber-500 px-1 text-[9px] font-bold text-slate-950"
                >
                  {pending > 9 ? "9+" : pending}
                </span>
              ) : null}
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}

export function BottomNav() {
  if (typeof document === "undefined") return null;
  return createPortal(<BottomNavBar />, document.body);
}
